import {
  UPDATE_VALUE,
  SYNC_ERRORS,
  REGISTER_FORM,
  REGISTER_FIELDS,
  RESET_FORM,
  RESET_FIELD,
  UNREGISTER_FIELDS,
  UPDATE_FIELD_META
} from "./types";
import { validate } from "@/utils/validations";
import cloneDeep from "lodash/cloneDeep";
import omit from "lodash/omit";

export default {
  /**
   * Registering form
   * @param commit
   * @param dispatch
   * @param formName
   * @param fields
   */
  registerForm({ commit, dispatch }, { formName, fields }) {
    commit({
      type: REGISTER_FORM,
      payload: {
        formName
      }
    });

    dispatch("registerFields", { formName, fields });
  },

  /**
   * Initializing form with values.
   * Update only if `field` exists in specified form.
   * @param dispatch
   * @param formName {String}
   * @param fields {Object}
   */
  initializeForm({ dispatch }, { formName, fields }) {
    if (Object.keys(fields).length) {
      for (let field in fields) {
        dispatch("updateValue", {
          formName,
          fieldName: field,
          value: fields[field]
        });
      }
    }
  },

  /**
   * Validate one field with provided function `validate` during initialization.
   * @param commit
   * @param state
   * @param formName
   * @param fieldName
   */
  validateField({ commit, state }, { formName, fieldName }) {
    const fields = state[formName].fields;
    const values = state[formName].values;

    const field = fields[fieldName];
    const value = values[fieldName];

    if (!field) {
      return;
    }

    const { validate: validateFunc } = field;
    const errors = validate(value, validateFunc, values);

    if (errors) {
      commit({
        type: SYNC_ERRORS,
        payload: {
          formName,
          errors,
          fieldName: fieldName,
          submitted: true
        }
      });
    }
  },

  /**
   * Validate fields with provided `validate` function during initialization.
   * Update errors in store as well.
   * @param commit
   * @param state
   * @param formName
   * @param ignorePristine
   */
  validateFields({ commit, state }, { formName, ignorePristine }) {
    let fields = cloneDeep(state[formName].fields);
    const values = state[formName].values;

    // ignoring non-touched fields
    // so they won't be checked for errors until being touched
    if (ignorePristine) {
      const keysToRemove = Object.keys(fields).filter(fieldKey => {
        const fieldMeta = fields[fieldKey].meta;
        return !fieldMeta.focused && !fieldMeta.blurred;
      });

      fields = omit(fields, keysToRemove);
    }

    for (let field in fields) {
      const { validate: validateFunc } = fields[field];
      const errors = validate(values[field], validateFunc, values);

      if (errors) {
        commit({
          type: SYNC_ERRORS,
          payload: {
            formName,
            errors,
            fieldName: field,
            submitted: true
          }
        });
      }
    }
  },

  /**
   * Register fields in particular form
   * @param commit
   * @param formName
   * @param fields
   */
  registerFields({ commit }, { formName, fields }) {
    commit({
      type: REGISTER_FIELDS,
      payload: {
        formName,
        fields
      }
    });
  },

  /**
   * Batch updating values in provided form name.
   * Updates only if form has been registered.
   * @param dispatch
   * @param formName
   * @param fields
   */
  updateValues({ dispatch }, { formName, fields }) {
    for (let field in fields) {
      dispatch("updateValue", {
        formName,
        fieldName: field,
        value: fields[field]
      });
    }
  },

  /**
   * Update particular field in specified form.
   * @param commit
   * @param formName
   * @param fieldName
   * @param value
   */
  updateValue({ commit }, { formName, fieldName, value }) {
    commit({
      type: UPDATE_VALUE,
      payload: {
        formName,
        fieldName,
        value
      }
    });
  },

  /**
   * Synchronize errors in form for particular field in specified form.
   * Adding `errors` array to form.
   * @param commit
   * @param formName
   * @param fieldName
   * @param errors
   */
  syncErrors({ commit }, { formName, fieldName, errors }) {
    commit({
      type: SYNC_ERRORS,
      payload: {
        formName,
        fieldName,
        errors
      }
    });
  },

  /**
   * Reset values/errors in particular form.
   * @param commit
   * @param formName
   */
  resetForm({ commit }, { formName }) {
    commit({
      type: RESET_FORM,
      payload: { formName }
    });
  },

  /**
   * Reset value/errors in particular form field.
   * @param commit
   * @param formName
   * @param fieldName
   */
  resetField({ commit }, { formName, fieldName }) {
    commit({
      type: RESET_FIELD,
      payload: { formName, fieldName }
    });
  },

  /**
   * Reset all of the errors from the given formName fields
   * @param commit
   * @param state
   * @param dispatch
   * @param formName
   */
  resetErrors({ commit, state, dispatch }, { formName }) {
    const { fields } = state[formName];

    for (let field in fields) {
      dispatch("syncErrors", { formName, fieldName: field, errors: [] });
    }
  },

  /**
   * Unregister fields from form
   * @param commit
   * @param formName
   * @param fieldsNames
   */
  unregisterFields({ commit }, { formName, fieldsNames }) {
    commit({
      type: UNREGISTER_FIELDS,
      payload: { formName, fieldsNames }
    });
  },

  /**
   * Update meta for a particular field
   * @param commit
   * @param formName
   * @param fieldName
   * @param metaKey
   * @param metaValue
   */
  updateFieldMeta({ commit }, { formName, fieldName, metaKey, metaValue }) {
    commit({
      type: UPDATE_FIELD_META,
      payload: { formName, fieldName, metaKey, metaValue }
    });
  }
};
